import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
  CheckCircle, Users, Activity, Clock, Target, 
  PieChart, TrendingUp, Download, Filter, Star,
  Calendar, CalendarDays, ChevronDown, BarChart3,
  Eye, TrendingDown, Award, Zap, Loader, X,
  PlayCircle, CheckSquare, MessageSquare, AlertCircle
} from 'lucide-react';
import { Query } from 'appwrite';
import { databases } from '../../../services/appwriteService';
import EnhancedStatCard from '../components/EnhancedStatCard';
import { GlobalStats, DetailedAuditorStats, SessionProgress } from '../types/AdminTypes';
import { useDailyReports } from '../hooks/useDailyReports';

interface EnhancedOverviewProps {
  globalStats: GlobalStats;
  auditorStats: DetailedAuditorStats[];
  sessions: SessionProgress[];
}

interface SessionWithAgent extends SessionProgress {
  agent_email: string;
  email_counts: Record<string, number>;
}

interface FilterPreset {
  label: string;
  value: string;
  from: string;
  to: string;
}

// ✅ NOUVEAU COMPOSANT - Modal de détails des sessions (VERSION CORRIGÉE)
const SessionDetailsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  auditorName: string;
  date: string;
  auditorId: string;
  dateFilter: string;
}> = ({ isOpen, onClose, auditorName, date, auditorId, dateFilter }) => {
  const [localSessions, setLocalSessions] = useState<SessionProgress[]>([]);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // ✅ CHARGEMENT MANUEL des sessions spécifiques au modal
  // ✅ CORRIGER la fonction loadModalSessions dans le modal
const loadModalSessions = useCallback(async () => {
  if (!isOpen || !auditorId || !dateFilter) return;
  
  setLocalLoading(true);
  setLocalError(null);
  
  try {
    console.log('🔍 [MODAL] Chargement sessions pour:', { auditorId, dateFilter });
    
    // ✅ CORRECTION - Utiliser $createdAt au lieu de audit_date
    const startOfDay = dateFilter + 'T00:00:00.000Z';
    const endOfDay = dateFilter + 'T23:59:59.999Z';
    
    console.log('📅 [MODAL] Période recherchée:', { startOfDay, endOfDay });
    
    const queries = [
      Query.equal('auditor_id', auditorId),
      Query.greaterThanEqual('$createdAt', startOfDay),
      Query.lessThanEqual('$createdAt', endOfDay),
      Query.orderDesc('$createdAt'),
      Query.limit(50) // ✅ Augmenter la limite
    ];

    const sessionsResponse = await databases.listDocuments(
      'focal-db',
      'audit_sessions',
      queries
    );

    console.log('📊 [MODAL] Sessions trouvées:', sessionsResponse.documents.length);
    console.log('📊 [MODAL] Sessions détails:', sessionsResponse.documents.map(s => ({
      id: s.$id,
      created: s.$createdAt,
      audit_date: s.audit_date,
      status: s.session_status
    })));

    // ✅ FALLBACK - Si pas de résultats avec $createdAt, essayer avec audit_date
    if (sessionsResponse.documents.length === 0) {
      console.log('🔄 [MODAL] Essai avec audit_date...');
      
      const fallbackQueries = [
        Query.equal('auditor_id', auditorId),
        Query.equal('audit_date', dateFilter),
        Query.orderDesc('$createdAt'),
        Query.limit(50)
      ];

      const fallbackResponse = await databases.listDocuments(
        'focal-db',
        'audit_sessions',
        fallbackQueries
      );
      
      console.log('📊 [MODAL] Sessions avec audit_date:', fallbackResponse.documents.length);
      sessionsResponse.documents = fallbackResponse.documents;
    }

    // ✅ FALLBACK 2 - Si toujours pas de résultats, chercher toutes les sessions de l'auditeur
    if (sessionsResponse.documents.length === 0) {
      console.log('🔄 [MODAL] Recherche toutes les sessions de l\'auditeur...');
      
      const allSessionsQueries = [
        Query.equal('auditor_id', auditorId),
        Query.orderDesc('$createdAt'),
        Query.limit(100)
      ];

      const allSessionsResponse = await databases.listDocuments(
        'focal-db',
        'audit_sessions',
        allSessionsQueries
      );
      
      console.log('📊 [MODAL] Toutes les sessions de l\'auditeur:', allSessionsResponse.documents.length);
      console.log('📊 [MODAL] Dates disponibles:', allSessionsResponse.documents.map(s => ({
        id: s.$id,
        created: s.$createdAt?.split('T')[0],
        audit_date: s.audit_date,
        status: s.session_status
      })));
      
      // Filtrer manuellement par date
      const filteredSessions = allSessionsResponse.documents.filter(session => {
        const sessionCreatedDate = session.$createdAt?.split('T')[0];
        const sessionAuditDate = session.audit_date;
        return sessionCreatedDate === dateFilter || sessionAuditDate === dateFilter;
      });
      
      console.log('📊 [MODAL] Sessions filtrées manuellement:', filteredSessions.length);
      sessionsResponse.documents = filteredSessions;
    }

    // Traitement des sessions trouvées
    const processedSessions = await Promise.all(
      sessionsResponse.documents.map(async (session) => {
        try {
          console.log(`📋 [MODAL] Traitement session ${session.$id}...`);
          
          // Récupérer les tâches de la session AVEC agent_email
          const tasksResponse = await databases.listDocuments(
            'focal-db',
            'audit_tasks',
            [
              Query.equal('session_id', session.$id),
              Query.limit(1000),
              Query.select(['$id', 'audit_status', 'agent_email']) // ✅ AJOUT agent_email
            ]
          );
    
          const tasks = tasksResponse.documents || [];
          const totalTasks = tasks.length;
          const completedTasks = tasks.filter(t => t.audit_status !== 'pending').length;
          const validatedTasks = tasks.filter(t => t.audit_status === 'approved').length;
          const rejectedTasks = tasks.filter(t => t.audit_status === 'rejected').length;
          const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    
          // ✅ RÉCUPÉRER L'EMAIL DE L'AGENT (premier email trouvé dans les tâches)
          const agentEmail = tasks.length > 0 ? tasks[0].agent_email || 'Email non disponible' : 'Aucune tâche';
          
          // ✅ COMPTER LES TÂCHES PAR AGENT EMAIL (au cas où il y en aurait plusieurs)
          const emailCounts = tasks.reduce((acc, task) => {
            const email = task.agent_email || 'Non défini';
            acc[email] = (acc[email] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);
          
          console.log(`📊 [MODAL] Session ${session.$id}: ${totalTasks} tâches pour ${agentEmail}`);
          console.log(`📧 [MODAL] Répartition emails:`, emailCounts);
    
          return {
            session_id: session.$id,
            agent_email: agentEmail, // ✅ AJOUT du champ agent_email
            email_counts: emailCounts, // ✅ AJOUT des comptages par email
            auditor: {
              id: auditorId,
              full_name: auditorName,
              email: ''
            },
            session_status: session.session_status,
            created_at: session.$createdAt,
            total_tasks: totalTasks,
            completed_tasks: completedTasks,
            validated_tasks: validatedTasks,
            rejected_tasks: rejectedTasks,
            progress_percentage: progressPercentage,
            avg_time_per_task: 0,
            current_task_id: undefined
          } as SessionProgress & { 
            agent_email: string; 
            email_counts: Record<string, number>; 
          };
    
        } catch (error) {
          console.error('❌ [MODAL] Erreur session:', error);
          return null;
        }
      })
    );

    const validSessions = processedSessions.filter(Boolean) as SessionProgress[];
    setLocalSessions(validSessions);
    console.log('✅ [MODAL] Sessions processées:', validSessions.length);
    
  } catch (error) {
    console.error('❌ [MODAL] Erreur chargement:', error);
    setLocalError('Erreur lors du chargement des sessions');
  } finally {
    setLocalLoading(false);
  }
}, [isOpen, auditorId, dateFilter, auditorName]);

  // Charger les sessions quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      loadModalSessions();
    } else {
      setLocalSessions([]);
      setLocalError(null);
    }
  }, [isOpen, loadModalSessions]);

  // Calculer les totaux
  const totals = useMemo(() => {
    return localSessions.reduce((acc, session) => {
      acc.totalTasks += session.total_tasks;
      acc.totalValidated += session.validated_tasks;
      acc.totalRejected += session.rejected_tasks;
      acc.totalCompleted += session.completed_tasks;
      acc.totalPending += (session.total_tasks - session.completed_tasks);
      return acc;
    }, {
      totalTasks: 0,
      totalValidated: 0,
      totalRejected: 0,
      totalCompleted: 0,
      totalPending: 0
    });
  }, [localSessions]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Overlay */}
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
  <div className="flex items-center justify-between">
    <div>
      <h2 className="text-2xl font-bold">Détails des Sessions</h2>
      <p className="text-blue-100 mt-1">
        {auditorName} {/* ✅ AJOUT de l'email si disponible */}
        {localSessions.length > 0 && localSessions[0].auditor.email && (
          <span className="text-blue-200"> • {localSessions[0].auditor.email}</span>
        )}
      </p>
      <p className="text-blue-200 text-sm">
        {new Date(date).toLocaleDateString('fr-FR', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
      </p>
    </div>
    <button
      onClick={onClose}
      className="p-2 hover:bg-white/20 rounded-xl transition-colors"
    >
      <X className="w-6 h-6" />
    </button>
  </div>
</div>

          {/* Content */}
          <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
            {localLoading ? (
              <div className="text-center py-12">
                <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">Chargement des détails des sessions...</p>
              </div>
            ) : localError ? (
              <div className="text-center py-12">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <p className="text-lg font-medium text-red-600">Erreur de chargement</p>
                <p className="text-red-500">{localError}</p>
                <button
                  onClick={loadModalSessions}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Réessayer
                </button>
              </div>
            ) : localSessions.length > 0 ? (
              <>
                {/* Résumé global */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{localSessions.length}</div>
                    <div className="text-sm text-gray-600">Sessions</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{totals.totalTasks}</div>
                    <div className="text-sm text-blue-600">Total Tâches</div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{totals.totalValidated}</div>
                    <div className="text-sm text-green-600">✅ Validées</div>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-orange-600">{totals.totalRejected}</div>
                    <div className="text-sm text-orange-600">💬 Commentées</div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 text-center">
                    <div className="text-2xl font-bold text-gray-600">{totals.totalPending}</div>
                    <div className="text-sm text-gray-600">⏳ En Attente</div>
                  </div>
                </div>

                {/* Barre de progression globale */}
                <div className="mb-8">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Progression Globale</span>
                    <span className="font-bold">
                      {totals.totalCompleted}/{totals.totalTasks} ({Math.round((totals.totalCompleted / Math.max(totals.totalTasks, 1)) * 100)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-blue-500 h-4 rounded-full transition-all duration-1000 relative overflow-hidden"
                      style={{ width: `${(totals.totalCompleted / Math.max(totals.totalTasks, 1)) * 100}%` }}
                    >
                      <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                    </div>
                  </div>
                </div>

                {/* Liste détaillée des sessions */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Sessions Détaillées</h3>
                  
                  {localSessions.map((session, index) => (
  <div key={session.session_id} className="bg-gray-50 rounded-xl p-5 border border-gray-200">
    {/* Header de session */}
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
          <span className="text-white font-bold">#{index + 1}</span>
        </div>
        <div>
          <h4 className="font-bold text-gray-900">Session {index + 1}</h4>
          <p className="text-sm text-gray-600">
            Créée à {new Date(session.created_at).toLocaleTimeString('fr-FR')}
          </p>
          {/* ✅ AJOUT - Affichage de l'email de l'agent */}
          <div className="flex items-center gap-2 mt-1">
            <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs">@</span>
            </div>
            <span className="text-sm font-medium text-blue-700">
              {(session as any).agent_email}
            </span>
          </div>
          
          {/* ✅ AJOUT - Si plusieurs emails dans la session, les afficher */}
          {(session as any).email_counts && Object.keys((session as any).email_counts).length > 1 && (
            <div className="mt-2">
              <p className="text-xs text-orange-600 font-medium">Plusieurs agents détectés :</p>
              <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries((session as any).email_counts).map(([email, count]) => (
                <span 
                  key={email}
                  className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full"
                >
                  {email}: {Number(count)} tâches
                </span>
              ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={`px-3 py-1 rounded-full text-sm font-bold ${
        session.session_status === 'completed' ? 'bg-green-100 text-green-800' :
        session.session_status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
        'bg-yellow-100 text-yellow-800'
      }`}>
        {session.session_status === 'completed' ? '✅ Terminée' :
         session.session_status === 'in_progress' ? '🔄 En cours' : '⏸️ En pause'}
      </div>
    </div>

                      {/* Métriques de session */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-white rounded-lg p-3 text-center border">
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <BarChart3 className="w-4 h-4 text-blue-500" />
                            <span className="text-xl font-bold text-blue-600">{session.total_tasks}</span>
                          </div>
                          <div className="text-xs text-gray-600">Total Tâches</div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3 text-center border">
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <CheckSquare className="w-4 h-4 text-green-500" />
                            <span className="text-xl font-bold text-green-600">{session.validated_tasks}</span>
                          </div>
                          <div className="text-xs text-gray-600">Validées</div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3 text-center border">
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <MessageSquare className="w-4 h-4 text-orange-500" />
                            <span className="text-xl font-bold text-orange-600">{session.rejected_tasks}</span>
                          </div>
                          <div className="text-xs text-gray-600">Commentées</div>
                        </div>
                        
                        <div className="bg-white rounded-lg p-3 text-center border">
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span className="text-xl font-bold text-gray-600">
                              {session.total_tasks - session.completed_tasks}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600">En Attente</div>
                        </div>
                      </div>

                      {/* Barre de progression de session */}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs mb-2">
                          <span className="text-gray-600">Progression Session</span>
                          <span className="font-bold">
                            {session.completed_tasks}/{session.total_tasks} ({session.progress_percentage}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all duration-500 ${
                              session.session_status === 'completed' ? 'bg-gradient-to-r from-green-400 to-green-600' :
                              session.session_status === 'in_progress' ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                              'bg-gradient-to-r from-yellow-400 to-yellow-600'
                            }`}
                            style={{ width: `${session.progress_percentage}%` }}
                          />
                        </div>
                      </div>

                      {/* Métriques supplémentaires */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1 text-gray-600">
                            <Target className="w-4 h-4" />
                            <span>Taux: {Math.round((session.validated_tasks / Math.max(session.completed_tasks, 1)) * 100)}%</span>
                          </div>
                        </div>
                        
                        {session.current_task_id && (
                          <div className="flex items-center gap-1 text-blue-600">
                            <PlayCircle className="w-4 h-4" />
                            <span>Tâche en cours</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-500">Aucune session trouvée</p>
                <p className="text-gray-400">Aucune session d'audit pour cette date</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EnhancedOverview: React.FC<EnhancedOverviewProps> = ({ 
  globalStats, 
  auditorStats, 
  sessions 
}) => {
  // États pour les filtres
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAuditor, setSelectedAuditor] = useState('all');
  const [sortBy, setSortBy] = useState('audits_count');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // ✅ NOUVEAUX ÉTATS pour le modal
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedSessionDetails, setSelectedSessionDetails] = useState<{
    auditorName: string;
    date: string;
    auditorId: string;
    dateFilter: string;
  } | null>(null);

  // Presets de filtres
  const filterPresets: FilterPreset[] = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    return [
      { label: "📅 Aujourd'hui", value: 'today', from: today, to: today },
      { label: "⏮️ Hier", value: 'yesterday', from: yesterday, to: yesterday },
      { label: "📊 Cette semaine", value: 'week', from: weekAgo, to: today },
      { label: "📈 Ce mois", value: 'month', from: monthAgo, to: today },
      { label: "🎯 Personnalisé", value: 'custom', from: '', to: '' }
    ];
  }, []);

  // Déterminer les dates à utiliser
  const { dateFrom, dateTo } = useMemo(() => {
    if (selectedPeriod === 'custom') {
      return { dateFrom: customDateFrom, dateTo: customDateTo };
    }
    const preset = filterPresets.find(p => p.value === selectedPeriod);
    return { dateFrom: preset?.from || '', dateTo: preset?.to || '' };
  }, [selectedPeriod, customDateFrom, customDateTo, filterPresets]);

  // ✅ UTILISATION DU NOUVEAU HOOK AVEC VRAIES DONNÉES
  const { dailyReports, loading: reportsLoading, error: reportsError, loadingProgress, refetch } = useDailyReports({
    dateFrom,
    dateTo,
    auditorId: selectedAuditor
  });

  // Filtrage et tri des rapports
  const filteredReports = useMemo(() => {
    let filtered = dailyReports;
    
    if (selectedAuditor !== 'all') {
      filtered = filtered.filter(report => report.auditor_id === selectedAuditor);
    }
    
    // Tri
    filtered.sort((a, b) => {
      const aValue = a[sortBy as keyof typeof a] as number;
      const bValue = b[sortBy as keyof typeof b] as number;
      
      if (sortOrder === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
    
    return filtered;
  }, [dailyReports, selectedAuditor, sortBy, sortOrder]);

  // Calcul des métriques agrégées pour la période sélectionnée
  const periodMetrics = useMemo(() => {
    const totalAudits = filteredReports.reduce((sum, report) => sum + report.audits_count, 0);
    const totalSessions = filteredReports.reduce((sum, report) => sum + report.sessions_count, 0);
    const avgValidationRate = filteredReports.length > 0 
      ? filteredReports.reduce((sum, report) => sum + report.validation_rate, 0) / filteredReports.length 
      : 0;
    const avgProductivity = filteredReports.length > 0
      ? filteredReports.reduce((sum, report) => sum + report.productivity_score, 0) / filteredReports.length
      : 0;
    
    // Grouper par auditeur pour calculer les moyennes
    const auditorSummary = filteredReports.reduce((acc, report) => {
      if (!acc[report.auditor_id]) {
        acc[report.auditor_id] = {
          name: report.auditor_name,
          totalAudits: 0,
          totalSessions: 0,
          days: 0
        };
      }
      acc[report.auditor_id].totalAudits += report.audits_count;
      acc[report.auditor_id].totalSessions += report.sessions_count;
      acc[report.auditor_id].days += 1;
      return acc;
    }, {} as Record<string, any>);

    const topPerformer = Object.entries(auditorSummary).reduce((top, [id, data]: [string, any]) => {
      const avgPerDay = data.days > 0 ? data.totalAudits / data.days : 0;
      if (avgPerDay > (top.avgPerDay || 0)) {
        return { id, name: data.name, avgPerDay };
      }
      return top;
    }, {} as any);

    return {
      totalAudits,
      totalSessions,
      avgValidationRate: Math.round(avgValidationRate),
      avgProductivity: Math.round(avgProductivity),
      topPerformer: topPerformer.name || 'N/A',
      topPerformerAvg: Math.round(topPerformer.avgPerDay || 0),
      uniqueAuditors: Object.keys(auditorSummary).length,
      totalDays: filteredReports.length > 0 ? Math.max(...Object.values(auditorSummary).map((a: any) => a.days)) : 0
    };
  }, [filteredReports]);

  // ✅ NOUVELLE FONCTION - Ouvrir le modal des détails de session
  const handleOpenSessionDetails = useCallback((report: any) => {
    setSelectedSessionDetails({
      auditorName: report.auditor_name,
      date: report.date,
      auditorId: report.auditor_id,
      dateFilter: report.date
    });
    setShowSessionModal(true);
  }, []);

  const exportData = async (type: string) => {
    console.log(`Export ${type}`, filteredReports);
    // Ici vous pouvez ajouter la logique d'export réelle
  };

  const applyPreset = useCallback((preset: FilterPreset) => {
    setSelectedPeriod(preset.value);
    if (preset.value !== 'custom') {
      setCustomDateFrom(preset.from);
      setCustomDateTo(preset.to);
    }
  }, []);

  // Affichage du loader si les données sont en cours de chargement
  if (reportsLoading) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {loadingProgress.stage === 'init' && '🚀 Initialisation...'}
              {loadingProgress.stage === 'auditors' && '👥 Chargement des auditeurs...'}
              {loadingProgress.stage === 'tasks' && '📋 Récupération des tâches...'}
              {loadingProgress.stage === 'sessions' && '🎯 Récupération des sessions...'}
              {loadingProgress.stage === 'processing' && '⚙️ Traitement des données...'}
              {loadingProgress.stage === 'finalizing' && '✨ Finalisation...'}
              {loadingProgress.stage === 'complete' && '✅ Terminé !'}
              {!loadingProgress.stage && 'Chargement des données réelles...'}
            </h3>
            
            <p className="text-gray-600 mb-4">{loadingProgress.message}</p>
            
            {/* Barre de progression */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div 
                className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${loadingProgress.progress}%` }}
              />
            </div>
            
            <div className="flex justify-between text-sm text-gray-500 mb-4">
              <span>Progression</span>
              <span>{Math.round(loadingProgress.progress)}%</span>
            </div>
            
            {/* Détails des batches si disponibles */}
            {loadingProgress.currentBatch && loadingProgress.totalBatches && (
              <div className="text-xs text-gray-500">
                Lot {loadingProgress.currentBatch} sur {loadingProgress.totalBatches}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Affichage de l'erreur si il y en a une
  if (reportsError) {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <div className="text-red-500 text-2xl">⚠️</div>
            <div>
              <h3 className="text-lg font-bold text-red-900 mb-2">Erreur de chargement</h3>
              <p className="text-red-700">{reportsError}</p>
              <button
                onClick={refetch}
                className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header avec filtres */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              📊 Rapports Journaliers par Auditeur
            </h1>
            <p className="text-gray-600">
              Performance détaillée • {periodMetrics.totalAudits} audits • {periodMetrics.uniqueAuditors} auditeurs actifs
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filtres
              <ChevronDown className={`w-4 h-4 transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            
            <button
onClick={() => exportData('daily-reports')}
className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 transition-colors"
>
<Download className="w-4 h-4" />
Export
</button>
</div>
</div>

{/* Filtres détaillés */}
{showFilters && (
<div className="mt-6 p-4 bg-gray-50 rounded-xl space-y-4">
<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
{/* Sélecteur de période */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
  <select
    value={selectedPeriod}
    onChange={(e) => applyPreset(filterPresets.find(p => p.value === e.target.value)!)}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
  >
    {filterPresets.map(preset => (
      <option key={preset.value} value={preset.value}>
        {preset.label}
      </option>
    ))}
  </select>
</div>

{/* Dates personnalisées */}
{selectedPeriod === 'custom' && (
  <>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Date début</label>
      <input
        type="date"
        value={customDateFrom}
        onChange={(e) => setCustomDateFrom(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Date fin</label>
      <input
        type="date"
        value={customDateTo}
        onChange={(e) => setCustomDateTo(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      />
    </div>
  </>
)}

{/* Sélecteur d'auditeur */}
<div>
  <label className="block text-sm font-medium text-gray-700 mb-2">Auditeur</label>
  <select
    value={selectedAuditor}
    onChange={(e) => setSelectedAuditor(e.target.value)}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
  >
    <option value="all">Tous les auditeurs</option>
    {auditorStats.map(auditor => (
      <option key={auditor.auditor_id} value={auditor.auditor_id}>
        {auditor.auditor.full_name}
      </option>
    ))}
  </select>
</div>
</div>

{/* Options de tri */}
<div className="flex flex-wrap items-center gap-4">
<div className="flex items-center gap-2">
  <label className="text-sm font-medium text-gray-700">Trier par:</label>
  <select
    value={sortBy}
    onChange={(e) => setSortBy(e.target.value)}
    className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
  >
    <option value="audits_count">Nombre d'audits</option>
    <option value="sessions_count">Nombre de sessions</option>
    <option value="validation_rate">Taux de validation</option>
    <option value="productivity_score">Score de productivité</option>
    <option value="date">Date</option>
  </select>
</div>

<div className="flex items-center gap-2">
  <label className="text-sm font-medium text-gray-700">Ordre:</label>
  <select
    value={sortOrder}
    onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
    className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
  >
    <option value="desc">Décroissant</option>
    <option value="asc">Croissant</option>
  </select>
</div>
</div>
</div>
)}
</div>

{/* Métriques de la période sélectionnée */}
<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
<EnhancedStatCard
title="Audits Total"
value={periodMetrics.totalAudits}
icon={Activity}
gradient="from-blue-500 to-cyan-500"
trend={15}
description={`${periodMetrics.totalDays} jour(s)`}
percentage={Math.min((periodMetrics.totalAudits / 1000) * 100, 100)}
/>

<EnhancedStatCard
title="Sessions Total"
value={periodMetrics.totalSessions}
icon={Clock}
gradient="from-green-500 to-emerald-500"
trend={8}
description={`${periodMetrics.uniqueAuditors} auditeurs`}
percentage={Math.min((periodMetrics.totalSessions / 100) * 100, 100)}
/>

<EnhancedStatCard
title="Validation Moyenne"
value={`${periodMetrics.avgValidationRate}%`}
icon={CheckCircle}
gradient="from-purple-500 to-pink-500"
trend={periodMetrics.avgValidationRate >= 80 ? 5 : -3}
description="Taux global"
percentage={periodMetrics.avgValidationRate}
/>

<EnhancedStatCard
title="Top Performer"
value={periodMetrics.topPerformerAvg}
icon={Award}
gradient="from-orange-500 to-red-500"
trend={12}
description={periodMetrics.topPerformer}
percentage={Math.min((periodMetrics.topPerformerAvg / 50) * 100, 100)}
/>
</div>

{/* Rapport de résumé par auditeur (non détaillé) */}
<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
<div className="flex items-center justify-between mb-6">
<h3 className="text-xl font-bold text-gray-900">
📋 Résumé par Auditeur - Période Sélectionnée
</h3>
<div className="flex items-center gap-2 text-sm text-gray-500">
<Eye className="w-4 h-4" />
Vue agrégée
</div>
</div>

{/* Calcul des données agrégées par auditeur */}
{(() => {
const auditorSummaries = filteredReports.reduce((acc, report) => {
if (!acc[report.auditor_id]) {
acc[report.auditor_id] = {
  auditor_id: report.auditor_id,
  auditor_name: report.auditor_name,
  total_audits: 0,
  total_sessions: 0,
  total_validated: 0,
  total_commented: 0,
  total_pending: 0,
  avg_validation_rate: 0,
  avg_productivity: 0,
  days_active: 0,
  best_day_audits: 0,
  avg_processing_time: 0,
  validation_rates: [],
  productivity_scores: [],
  processing_times: []
};
}

const summary = acc[report.auditor_id];
summary.total_audits += report.audits_count;
summary.total_sessions += report.sessions_count;
summary.total_validated += report.validated_tasks;
summary.total_commented += report.commented_tasks;
summary.total_pending += report.pending_tasks;
summary.days_active += 1;
summary.best_day_audits = Math.max(summary.best_day_audits, report.audits_count);

// Pour les moyennes
summary.validation_rates.push(report.validation_rate);
summary.productivity_scores.push(report.productivity_score);
if (report.avg_processing_time > 0) {
summary.processing_times.push(report.avg_processing_time);
}

return acc;
}, {} as Record<string, any>);

// Calculer les moyennes
Object.values(auditorSummaries).forEach((summary: any) => {
summary.avg_validation_rate = summary.validation_rates.length > 0
? Math.round(summary.validation_rates.reduce((a: number, b: number) => a + b, 0) / summary.validation_rates.length)
: 0;
summary.avg_productivity = summary.productivity_scores.length > 0
? Math.round(summary.productivity_scores.reduce((a: number, b: number) => a + b, 0) / summary.productivity_scores.length)
: 0;
summary.avg_processing_time = summary.processing_times.length > 0
? Math.round(summary.processing_times.reduce((a: number, b: number) => a + b, 0) / summary.processing_times.length)
: 0;
summary.daily_avg_audits = summary.days_active > 0 ? Math.round(summary.total_audits / summary.days_active) : 0;
summary.daily_avg_sessions = summary.days_active > 0 ? Math.round(summary.total_sessions / summary.days_active) : 0;
});

const sortedSummaries = Object.values(auditorSummaries).sort((a: any, b: any) => b.total_audits - a.total_audits);

return sortedSummaries.length > 0 ? (
<div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
{sortedSummaries.map((summary: any, index) => (
  <div key={summary.auditor_id} className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200 hover:shadow-md transition-all duration-300">
    {/* Badge de rang pour les 3 premiers */}
    {index < 3 && (
      <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
        <span className="text-white text-sm font-bold">#{index + 1}</span>
      </div>
    )}

    {/* Header auditeur */}
    <div className="flex items-center gap-3 mb-4">
      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
        <span className="text-white font-bold text-sm">
          {summary.auditor_name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
        </span>
      </div>
      <div>
        <h4 className="font-bold text-gray-900 text-lg">{summary.auditor_name}</h4>
        <p className="text-sm text-gray-600">{summary.days_active} jour(s) actif(s)</p>
      </div>
    </div>

    {/* Métriques principales */}
    <div className="grid grid-cols-2 gap-4 mb-4">
      <div className="bg-white rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-blue-600">{summary.total_audits}</div>
        <div className="text-xs text-gray-600">Total Audits</div>
        <div className="text-xs text-blue-500">~{summary.daily_avg_audits}/jour</div>
      </div>
      <div className="bg-white rounded-lg p-3 text-center">
        <div className="text-2xl font-bold text-green-600">{summary.total_sessions}</div>
        <div className="text-xs text-gray-600">Total Sessions</div>
        <div className="text-xs text-green-500">~{summary.daily_avg_sessions}/jour</div>
      </div>
    </div>

    {/* Répartition des tâches */}
    <div className="space-y-3 mb-4">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">✅ Validées</span>
        <span className="font-bold text-green-600">{summary.total_validated}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">💬 Commentées</span>
        <span className="font-bold text-blue-600">{summary.total_commented}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-gray-700">⏳ En attente</span>
        <span className="font-bold text-gray-600">{summary.total_pending}</span>
      </div>
    </div>

    {/* Barre de progression */}
    <div className="mb-4">
      <div className="flex justify-between text-xs mb-2">
        <span className="text-gray-600">Progression</span>
        <span className="font-bold text-gray-900">
          {summary.total_validated + summary.total_commented}/{summary.total_audits}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div 
          className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-1000 relative overflow-hidden"
          style={{
            width: `${summary.total_audits > 0 ? ((summary.total_validated + summary.total_commented) / summary.total_audits) * 100 : 0}%`
          }}
        >
          <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
        </div>
      </div>
    </div>

    {/* Métriques de performance */}
    <div className="flex justify-between items-center text-xs">
      <div className="text-center">
        <div className={`font-bold ${
          summary.avg_validation_rate >= 90 ? 'text-green-600' :
          summary.avg_validation_rate >= 70 ? 'text-blue-600' :
          summary.avg_validation_rate >= 50 ? 'text-yellow-600' :
          'text-red-600'
        }`}>
          {summary.avg_validation_rate}%
        </div>
        <div className="text-gray-500">Validation</div>
      </div>
      <div className="text-center">
        <div className="font-bold text-purple-600">{summary.avg_productivity}</div>
        <div className="text-gray-500">Productivité</div>
      </div>
      <div className="text-center">
        <div className="font-bold text-orange-600">{summary.best_day_audits}</div>
        <div className="text-gray-500">Meilleur jour</div>
      </div>
      {summary.avg_processing_time > 0 && (
        <div className="text-center">
          <div className="font-bold text-gray-600">{summary.avg_processing_time}min</div>
          <div className="text-gray-500">Temps moy.</div>
        </div>
      )}
    </div>

    {/* Badge de statut */}
    <div className="mt-3 flex justify-center">
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
        summary.avg_productivity >= 80 ? 'bg-green-100 text-green-800' :
        summary.avg_productivity >= 60 ? 'bg-blue-100 text-blue-800' :
        summary.avg_productivity >= 40 ? 'bg-yellow-100 text-yellow-800' :
        'bg-red-100 text-red-800'
      }`}>
        {summary.avg_productivity >= 80 ? '🌟 Excellent' :
         summary.avg_productivity >= 60 ? '👍 Bon' :
         summary.avg_productivity >= 40 ? '⚠️ Moyen' :
         '📉 À améliorer'}
      </span>
    </div>
  </div>
))}
</div>
) : (
<div className="text-center py-12">
<Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
<p className="text-lg font-medium text-gray-500">Aucune donnée agrégée</p>
<p className="text-gray-400">Aucun rapport trouvé pour la période sélectionnée</p>
</div>
);
})()}
</div>

{/* Tableau des rapports journaliers */}
<div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
<div className="flex items-center justify-between mb-6">
<h3 className="text-xl font-bold text-gray-900">
📅 Rapports Détaillés par Jour
</h3>
<span className="text-sm text-gray-500">
{filteredReports.length} entrée(s)
</span>
</div>

{filteredReports.length > 0 ? (
<div className="overflow-x-auto">
<table className="w-full text-sm">
<thead>
  <tr className="border-b border-gray-200 bg-gray-50">
    <th className="px-4 py-3 text-left font-medium text-gray-700">Date</th>
    <th className="px-4 py-3 text-left font-medium text-gray-700">Auditeur</th>
    <th className="px-4 py-3 text-center font-medium text-gray-700">Audits</th>
    <th className="px-4 py-3 text-center font-medium text-gray-700">Sessions</th>
    <th className="px-4 py-3 text-center font-medium text-gray-700">✅ Validées</th>
    <th className="px-4 py-3 text-center font-medium text-gray-700">💬 Commentées</th>
    <th className="px-4 py-3 text-center font-medium text-gray-700">⏳ En Attente</th>
    <th className="px-4 py-3 text-center font-medium text-gray-700">Taux Validation</th>
    <th className="px-4 py-3 text-center font-medium text-gray-700">Temps Moy.</th>
    <th className="px-4 py-3 text-center font-medium text-gray-700">Score</th>
  </tr>
</thead>
<tbody>
  {filteredReports.map((report, index) => (
    <tr key={`${report.date}-${report.auditor_id}`} className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400" />
          <span className="font-medium">
            {new Date(report.date).toLocaleDateString('fr-FR', {
              weekday: 'short',
              day: '2-digit',
              month: '2-digit'
            })}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xs">
              {report.auditor_name.split(' ').map(n => n[0]).join('').toUpperCase()}
            </span>
          </div>
          <span className="font-medium text-gray-900">{report.auditor_name}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-bold">
          <BarChart3 className="w-3 h-3" />
          {report.audits_count}
        </span>
      </td>
      {/* ✅ MODIFICATION - Colonne Sessions maintenant cliquable */}
      <td className="px-4 py-3 text-center">
        <button
          onClick={() => handleOpenSessionDetails(report)}
          className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full font-bold hover:bg-green-200 transition-colors cursor-pointer"
          title="Cliquez pour voir le détail des sessions"
        >
          <Zap className="w-3 h-3" />
          {report.sessions_count}
          <Eye className="w-3 h-3 ml-1" />
        </button>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-green-700 font-bold">{report.validated_tasks}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-blue-700 font-bold">{report.commented_tasks}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-gray-700 font-bold">{report.pending_tasks}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className={`inline-flex items-center px-2 py-1 rounded-full font-bold text-xs ${
          report.validation_rate >= 90 ? 'bg-green-100 text-green-800' :
          report.validation_rate >= 70 ? 'bg-blue-100 text-blue-800' :
          report.validation_rate >= 50 ? 'bg-yellow-100 text-yellow-800' :
          'bg-red-100 text-red-800'
        }`}>
          {report.validation_rate}%
        </span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-gray-600">{report.avg_processing_time}min</span>
      </td>
      <td className="px-4 py-3 text-center">
        <div className="flex items-center justify-center gap-1">
          <span className="font-bold text-gray-700">{report.productivity_score}</span>
          <div className="flex">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                className={`w-3 h-3 ${
                  i < Math.floor(report.productivity_score / 20) 
                    ? 'text-yellow-500 fill-current' 
                    : 'text-gray-300'
                }`} 
              />
            ))}
          </div>
        </div>
      </td>
    </tr>
  ))}
</tbody>
</table>
</div>
) : (
<div className="text-center py-12">
<CalendarDays className="w-16 h-16 text-gray-300 mx-auto mb-4" />
<p className="text-lg font-medium text-gray-500">Aucun rapport trouvé</p>
<p className="text-gray-400">Ajustez vos filtres ou vérifiez la période sélectionnée</p>
</div>
)}
</div>

<div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100">
<div className="flex items-center justify-between mb-6">
<h3 className="text-xl font-bold text-blue-900">📈 Résumé Global (toutes périodes)</h3>
<TrendingUp className="w-6 h-6 text-blue-600" />
</div>

<div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
<div className="text-center">
<div className="text-3xl font-bold text-blue-900 mb-1">{globalStats.totalTasks}</div>
<div className="text-blue-700 text-sm">Total Audits</div>
</div>
<div className="text-center">
<div className="text-3xl font-bold text-green-900 mb-1">{globalStats.validatedTasks}</div>
<div className="text-green-700 text-sm">✅ Approuvées</div>
</div>
<div className="text-center">
<div className="text-3xl font-bold text-blue-900 mb-1">{globalStats.commentedTasks}</div>
<div className="text-blue-700 text-sm">💬 Commentées</div>
</div>
<div className="text-center">
<div className="text-3xl font-bold text-gray-900 mb-1">{globalStats.pendingTasks}</div>
<div className="text-gray-700 text-sm">⏳ En Attente</div>
</div>
</div>
</div>

{/* ✅ NOUVEAU - Modal de détails des sessions */}
{selectedSessionDetails && (
<SessionDetailsModal
isOpen={showSessionModal}
onClose={() => {
setShowSessionModal(false);
setSelectedSessionDetails(null);
}}
auditorName={selectedSessionDetails.auditorName}
date={selectedSessionDetails.date}
auditorId={selectedSessionDetails.auditorId}
dateFilter={selectedSessionDetails.dateFilter}
/>
)}
</div>
);
};

export default EnhancedOverview;